import { offlineQueue } from './offlineQueueService';
import { loadPhotoAsFile } from './offlineFileService';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface PhotoEntry {
  data: string;       // base64 without data: prefix
  mime_type: string;
  sha256: string;
}

interface ExportManifest {
  version: string;
  app: string;
  exported_at: string;
  user_id: string;
  user_name: string;
  total_items: number;
  photos: Record<string, PhotoEntry>;
  items: ExportItem[];
  hmac_signature?: string;
}

interface ExportItem {
  id: string;
  user_id: string;
  action_type: string;
  client_timestamp: string;
  created_at: string;
  retry_count: number;
  error?: string;
  payload: Record<string, any>;
}

export interface ExportResult {
  filename: string;
  size: number;
  item_count: number;
  blob: Blob;
}

// ─────────────────────────────────────────────────────────────────────────────
// Web Crypto helpers (built-in browser API — no library needed)
// ─────────────────────────────────────────────────────────────────────────────

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(binary);
}

function hexEncode(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

async function sha256Hash(bytes: Uint8Array): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', bytes.buffer as ArrayBuffer);
  return hexEncode(hash);
}

async function deriveUserHmacKey(userId: string): Promise<CryptoKey> {
  const secret = process.env.REACT_APP_EXPORT_HMAC_SECRET || '';
  const enc = new TextEncoder();

  const masterKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  // Derive per-user raw key bytes
  const derivedBytes = await crypto.subtle.sign('HMAC', masterKey, enc.encode(userId));
  return crypto.subtle.importKey(
    'raw',
    derivedBytes,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
}

async function hmacSign(message: string, key: CryptoKey): Promise<string> {
  const enc = new TextEncoder();
  const sig = await crypto.subtle.sign('HMAC', key, enc.encode(message));
  return hexEncode(sig);
}

// ─────────────────────────────────────────────────────────────────────────────
// Photo processing
// ─────────────────────────────────────────────────────────────────────────────

async function embedPhoto(photoRef: any): Promise<{ key: string; entry: PhotoEntry } | null> {
  if (!photoRef) return null;
  try {
    const file = await loadPhotoAsFile(photoRef);
    const arrayBuffer = await file.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    const hash = await sha256Hash(bytes);
    const b64 = uint8ToBase64(bytes);
    const key = (photoRef as any).filename || `photo_${Date.now()}.jpg`;
    return {
      key,
      entry: { data: b64, mime_type: file.type || 'image/jpeg', sha256: hash },
    };
  } catch {
    return null;
  }
}

async function processPayload(
  payload: Record<string, any>,
  photos: Record<string, PhotoEntry>
): Promise<Record<string, any>> {
  const p = { ...payload };

  if (p.photo_ref) {
    const result = await embedPhoto(p.photo_ref);
    p.photo_ref = result ? (photos[result.key] = result.entry, result.key) : null;
  }

  if (Array.isArray(p.attachments)) {
    p.attachments = await Promise.all(
      p.attachments.map(async (att: any) => {
        if (!att.photo_ref) return att;
        const result = await embedPhoto(att.photo_ref);
        if (result) { photos[result.key] = result.entry; return { ...att, photo_ref: result.key }; }
        return { ...att, photo_ref: null };
      })
    );
  }

  if (Array.isArray(p.additional_files)) {
    p.additional_files = await Promise.all(
      p.additional_files.map(async (att: any) => {
        if (!att.photo_ref) return att;
        const result = await embedPhoto(att.photo_ref);
        if (result) { photos[result.key] = result.entry; return { ...att, photo_ref: result.key }; }
        return { ...att, photo_ref: null };
      })
    );
  }

  return p;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

export async function exportOfflineQueue(
  userId: string,
  userName: string
): Promise<ExportResult> {
  const allItems = await offlineQueue.getAll();
  const exportable = allItems.filter(i => i.status === 'pending' || i.status === 'failed');

  if (exportable.length === 0) {
    throw new Error('Tidak ada data antrian offline yang perlu diekspor.');
  }

  const photos: Record<string, PhotoEntry> = {};
  const items: ExportItem[] = [];

  for (const item of exportable) {
    const payload = await processPayload(item.payload, photos);
    items.push({
      id: item.id,
      user_id: item.user_id,
      action_type: item.action_type,
      client_timestamp: item.client_timestamp,
      created_at: item.created_at,
      retry_count: item.retry_count,
      error: item.error,
      payload,
    });
  }

  // Build manifest in deterministic key order (required for HMAC consistency)
  const manifest: ExportManifest = {
    version: '1',
    app: 'TeraTask',
    exported_at: new Date().toISOString(),
    user_id: userId,
    user_name: userName,
    total_items: items.length,
    photos,
    items,
  };

  const userKey = await deriveUserHmacKey(userId);
  const sig = await hmacSign(JSON.stringify(manifest), userKey);
  const signed = { ...manifest, hmac_signature: sig };

  const jsonContent = JSON.stringify(signed, null, 2);
  const encoded = new TextEncoder().encode(jsonContent);
  const blob = new Blob([jsonContent], { type: 'application/json' });

  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const safeName = userName.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 20);
  const filename = `teratask_export_${safeName}_${date}.json`;

  return { filename, size: encoded.length, item_count: items.length, blob };
}

export type ShareResult =
  | { type: 'shared' }
  | { type: 'saved'; path: string }
  | { type: 'downloaded' }
  | { type: 'needs_modal'; blob: Blob; filename: string };

/**
 * Bagikan file ke app lain.
 * - iOS Safari / browser modern: Web Share API dengan file
 * - Android Capacitor: simpan ke Downloads, lalu coba native share sheet
 * - Desktop: download biasa
 * - Jika semua gagal: kembalikan 'needs_modal' agar UI tampilkan opsi manual
 */
export async function shareOrDownload(blob: Blob, filename: string): Promise<ShareResult> {
  const isCapacitor = !!(window as any).Capacitor;
  const file = new File([blob], filename, { type: 'application/json' });

  if (isCapacitor) {
    // Simpan ke Downloads terlebih dahulu agar file tersedia di file manager
    try {
      const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem');
      await Filesystem.writeFile({
        path: `Download/${filename}`,
        data: await blob.text(),
        directory: Directory.ExternalStorage,
        encoding: Encoding.UTF8,
        recursive: true,
      });
    } catch {
      try {
        const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem');
        await Filesystem.writeFile({
          path: filename,
          data: await blob.text(),
          directory: Directory.Documents,
          encoding: Encoding.UTF8,
        });
      } catch {
        // Filesystem tidak bisa diakses — lanjut ke share saja
      }
    }

    // Coba native share sheet dengan file ter-attach (Android share intent)
    // Tidak pakai canShare() karena di beberapa WebView selalu false meski share-nya bisa
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ files: [file], title: filename });
        return { type: 'shared' };
      } catch (e) {
        if ((e as any)?.name === 'AbortError') {
          // User membatalkan — file sudah di Downloads
          return { type: 'saved', path: `Download/${filename}` };
        }
        // navigator.share({ files }) tidak didukung → tampilkan modal
      }
    }

    // Fallback: modal dengan instruksi share manual dari file manager
    return { type: 'needs_modal', blob, filename };
  }

  // iOS Safari / browser modern: Web Share API dengan file
  if (
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [file] })
  ) {
    await navigator.share({ files: [file], title: filename });
    return { type: 'shared' };
  }

  // Desktop fallback
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return { type: 'downloaded' };
}
